import { useDispatch } from 'react-redux';

import { toast } from '../../../util/toast';
import {
  useUpdatePaylinkMutation,
  paylinkApi,
  useDeleteFileMutation,
} from '../../../store/modules/paylink/api';
import { UpdatePaylinkBody } from '../../../store/modules/paylink/types';

type UsePaylinkUpdateProps = {
  paylinkId: string;
};

export const usePaylinkUpdate = ({ paylinkId }: UsePaylinkUpdateProps) => {
  const [updatePaylink, { isLoading }] = useUpdatePaylinkMutation();
  const [deleteFile, { isLoading: isDeletingFile }] = useDeleteFileMutation();
  const dispatch = useDispatch();

  const onPaylinkUpdate = async (
    updatePaylinkBody: UpdatePaylinkBody,
    attachmentsToDelete: string[],
  ) => {
    const hasPaylinkChanges = Object.keys(updatePaylinkBody).length > 0;
    const hasAttachmentsToDelete = attachmentsToDelete.length > 0;

    if (!hasPaylinkChanges && !hasAttachmentsToDelete) {
      toast.info('No changes were made.');
      return;
    }

    if (hasAttachmentsToDelete) {
      await Promise.all(
        attachmentsToDelete.map((attachmentId) =>
          deleteFile({
            paylinkId,
            attachmentId,
          })
            .unwrap()
            .then(() => {
              toast.success('Attached file successfully removed.');
              return true;
            })
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            .catch((err) => {
              const errorMessage = `Unable to remove file from the paylink. Please try again later.`;
              toast.error(errorMessage);
              return false;
            }),
        ),
      );
    }

    if (Object.keys(updatePaylinkBody).length > 0) {
      try {
        await updatePaylink({ paylinkId, body: updatePaylinkBody }).unwrap();

        // let update go through before refetching
        setTimeout(() => {
          dispatch(paylinkApi.util.invalidateTags([{ type: 'Paylink', id: paylinkId }]));
        }, 10);
      } catch {
        const errorMessage =
          'Something went wrong while editing the Paylink, please try again later';
        toast.error(errorMessage);
        return;
      }
      toast.success('Paylink successfully edited.');
    }
  };

  return {
    onPaylinkUpdate,
    isLoading,
    isDeletingFile,
  };
};
